import { Box } from 'components'
import { useCreditContext } from 'contexts'
import { CoreCapsuleWithAccount } from 'entities'
import { BigNumber } from 'ethers'
import { formatBigNumber, getSpacing } from 'utils'
import { IFutureValues } from './helpers/IFutureValues'
import { InfoItem } from './InfoItem'
export interface IUserBorrowStatsProps {
  futureValues: IFutureValues
  coreCapsule: CoreCapsuleWithAccount
  isExitTransaction: boolean
}

export const UserBorrowStats = ({
  coreCapsule,
  futureValues,
  isExitTransaction,
}: IUserBorrowStatsProps) => {
  const { poolCapsules } = useCreditContext()
  const {
    collateralAmount,
    borrowedAmountScaled: borrowedAmount,
    creditLimit,
    liquidationPrice,
  } = coreCapsule.accountProperties
  const poolCapsule = poolCapsules.get(coreCapsule.borrowPoolAddress)

  const borrowAsset = poolCapsule.supportedDepositAssets[0]
  const maxBorrowAmount = coreCapsule.getAccountMaxBorrowAmount(poolCapsule, borrowAsset.address)

  let userAppliedLimit = coreCapsule.defaultBorrowLimit

  if (BigNumber.from(creditLimit.proof?.score || 0).gt(userAppliedLimit)) {
    userAppliedLimit = BigNumber.from(creditLimit.proof.score)
  }

  const { futureDeposited, futureBorrowed, futureAvailable, futureLiquidationPrice } = futureValues

  let futureDepositedText = futureDeposited && formatBigNumber(futureDeposited, 3)
  let futureBorrowedText = futureBorrowed && '$' + formatBigNumber(futureBorrowed, 2)
  let futureAvailableText = futureAvailable && '$' + formatBigNumber(futureAvailable, 2)
  let futureLiquidationText =
    futureLiquidationPrice && '$' + formatBigNumber(futureLiquidationPrice, 2)

  if (isExitTransaction) {
    futureDepositedText = formatBigNumber(0, 3)
    futureBorrowedText = '$' + formatBigNumber(0, 2)
    futureAvailableText = '$' + formatBigNumber(0, 2)
    futureLiquidationText = '$' + formatBigNumber(0, 2)
  }

  return (
    <Box flexDirection="column" gap={getSpacing('s')}>
      <InfoItem
        label={`Deposited (${coreCapsule.collateralSymbol})`}
        value={formatBigNumber(collateralAmount, 3)}
        futureValue={futureDepositedText}
        singleLine
      />
      <InfoItem
        label="Borrowed (USD)"
        value={'$' + formatBigNumber(borrowedAmount, 2)}
        futureValue={futureBorrowedText}
        singleLine
      />
      <InfoItem
        label="Available (USD)"
        value={'$' + formatBigNumber(maxBorrowAmount, 2, borrowAsset.decimals)}
        futureValue={futureAvailableText}
        singleLine
        // eslint-disable-next-line max-len
        tooltip={`The maximum that can be borrowed given your deposited collateral, the available funds in the pool and your credit limit (currently $${formatBigNumber(
          userAppliedLimit,
          2,
        )}).`}
      />
      <InfoItem
        label="Liquidation price (USD)"
        value={'$' + formatBigNumber(liquidationPrice, 2)}
        futureValue={futureLiquidationText}
        singleLine
      />
    </Box>
  )
}
